import type {
  EtapasDoCaso,
  LocalComQuantidade,
  LocalDoEstoque,
  ResultadoDaBusca,
} from '../../auditoria';

/**
 * Divergências 3.0 — funções puras das telas de etapa (testadas em tests/etapas.test.ts).
 * O servidor valida tudo de novo; aqui só se evita o round-trip inútil e se escreve o texto.
 */

const unidade = (n: number) => (n === 1 ? 'unidade' : 'unidades');

/** "Buscar 3 unidades" — a coluna da aba Estoque. */
export const buscarUnidades = (alvo: number) => `Buscar ${String(alvo)} ${unidade(alvo)}`;
/** "LOCALIZAR 3 UNIDADES" — o título da tela do Igor. */
export const localizarUnidades = (alvo: number) =>
  `LOCALIZAR ${String(alvo)} ${unidade(alvo).toUpperCase()}`;

/** "−3" com o sinal de menos tipográfico; positivo ganha "+". */
export const sinalDe = (n: number) => (n < 0 ? `−${String(-n)}` : n > 0 ? `+${String(n)}` : '0');

/** "Balconista: 12 · Você: 9 · Diferença: −3" (diferença = a contagem nova − a do balconista). */
export function comparacaoDaPrateleira(balconista: number | null, voce: number | null): string {
  if (voce === null) return `Balconista: ${balconista === null ? '—' : String(balconista)}`;
  if (balconista === null) return `Balconista: — · Você: ${String(voce)}`;
  return `Balconista: ${String(balconista)} · Você: ${String(voce)} · Diferença: ${sinalDe(voce - balconista)}`;
}

/**
 * Unidades a procurar no estoque, com a contagem que está sendo digitada. Estoque inconsistente:
 * o que o HOS diz (`saldoHos`) e a gôndola não tem; nos outros, o alvo do caso.
 */
export const alvoComAContagem = (
  alvo: number,
  estoqueInconsistente: boolean,
  saldoHos: number,
  contagem: number | null,
) => (estoqueInconsistente ? (contagem === null ? 0 : Math.max(0, saldoHos - contagem)) : alvo);

/** O botão que libera a prateleira: sem unidades a procurar vai ao ajuste. */
export const rotuloDeLiberar = (alvo: number) =>
  alvo <= 0 ? 'Liberar para o ajuste' : 'Liberar para conferência do estoque';

/** Uma chave nova por AÇÃO do usuário; o reenvio da mesma ação reaproveita a mesma. */
export function novaChave(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const h = (n: number) =>
    Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${h(8)}-${h(4)}-4${h(3)}-a${h(3)}-${h(12)}`;
}

/** O que cada botão rápido da busca define (PARTE deixa a quantidade para quem digita). */
export function resultadoDoBotao(
  r: ResultadoDaBusca,
  alvo: number,
  atual: number | null,
): { localizado: number | null; limpaLocais: boolean } {
  if (r === 'TODAS') return { localizado: alvo, limpaLocais: false };
  if (r === 'PARTE')
    return {
      localizado: atual !== null && atual > 0 && atual < alvo ? atual : null,
      limpaLocais: false,
    };
  return { localizado: 0, limpaLocais: true };
}

export interface FormularioDoEstoque {
  readonly alvo: number;
  readonly resultado: ResultadoDaBusca | null;
  readonly localizado: number | null;
  readonly locais: readonly { readonly local: LocalDoEstoque | ''; readonly qtd: number | null }[];
}

/** null = pode enviar; senão, o que falta (a mesma regra do servidor, em português). */
export function problemaDoEstoque(f: FormularioDoEstoque): string | null {
  if (f.resultado === null) return 'Escolha o que aconteceu: encontrei todas, parte ou nenhuma.';
  if (f.localizado === null) return 'Diga quantas unidades você encontrou.';
  if (!Number.isInteger(f.localizado) || f.localizado < 0) return 'Quantidade inválida.';
  if (f.localizado > f.alvo)
    return `Você só procura ${String(f.alvo)} ${unidade(f.alvo)}: a quantidade passa disso.`;
  if (f.resultado === 'TODAS' && f.localizado !== f.alvo)
    return `“Encontrei todas” é ${String(f.alvo)} ${unidade(f.alvo)}.`;
  if (f.resultado === 'PARTE' && (f.localizado <= 0 || f.localizado >= f.alvo))
    return `“Encontrei parte” é mais que 0 e menos que ${String(f.alvo)}.`;
  if ((f.resultado === 'NENHUMA' || f.resultado === 'NAO_VERIFICOU') && f.localizado !== 0)
    return 'Sem unidades encontradas a quantidade é 0.';
  const preenchidos = f.locais.filter((l) => l.local !== '' || (l.qtd ?? 0) > 0);
  if (f.localizado === 0)
    return preenchidos.length > 0 ? 'Sem unidades não há local a informar.' : null;
  if (preenchidos.some((l) => l.local === '' || l.qtd === null || l.qtd < 1))
    return 'Cada local precisa de um lugar e de uma quantidade.';
  const vistos = new Set(preenchidos.map((l) => l.local));
  if (vistos.size !== preenchidos.length)
    return 'O mesmo local aparece duas vezes: junte as quantidades.';
  const soma = preenchidos.reduce((t, l) => t + (l.qtd ?? 0), 0);
  if (soma !== f.localizado)
    return `Os locais somam ${String(soma)}, mas você encontrou ${String(f.localizado)}.`;
  return null;
}

/** Os locais prontos para o corpo da requisição. */
export function locaisParaEnvio(f: FormularioDoEstoque): LocalComQuantidade[] {
  if (f.localizado === 0) return [];
  return f.locais.flatMap((l) =>
    l.local !== '' && l.qtd !== null && l.qtd > 0 ? [{ local: l.local, qtd: l.qtd }] : [],
  );
}

/** Quantas unidades ainda sem local (para o aviso "faltam 2"). */
export function faltaDistribuir(f: FormularioDoEstoque): number {
  const soma = f.locais.reduce((t, l) => t + (l.qtd ?? 0), 0);
  return (f.localizado ?? 0) - soma;
}

/** "Página 2 de 7" — nunca menos de 1 página. */
export const totalDePaginas = (total: number, porPagina: number) =>
  Math.max(1, Math.ceil(total / Math.max(1, porPagina)));

/** A diferença que um ajuste geraria: contagem física completa − saldo atual do HOS. */
export const diferencaDoAjuste = (fisicoTotal: number, saldoHos: number | null) =>
  saldoHos === null ? null : fisicoTotal - saldoHos;

/** Pendência de ajuste no HOS ainda sem decisão? */
export const temAjustePendente = (e: Pick<EtapasDoCaso, 'ajustes'>) =>
  e.ajustes.some((a) => a.estado === 'PENDENTE_NO_HOS');

/** A frase que separa o não localizado da falta do estoque inteiro. */
export const AVISO_NAO_LOCALIZAR = (alvo: number) =>
  `Não localizar as ${String(alvo)} ${unidade(alvo)} NÃO significa falta do estoque inteiro.`;

/* ------------------------------------------------------------------ *
 * DIVERGÊNCIA SIMPLES (dono, 13/10): prateleira (em cima) + estoque (embaixo) → foi erro ou não.
 * ------------------------------------------------------------------ */

export interface LeituraDasContagens {
  /** Prateleira + estoque; null enquanto falta uma das contagens (ou o estoque não foi verificado). */
  readonly fisicoTotal: number | null;
  /** O botão "Foi erro do balconista" pode ser usado (o servidor confere de novo). */
  readonly podeErro: boolean;
  /** Por que não dá para marcar erro (null quando dá). */
  readonly semErro: string | null;
  /** Uma frase para quem decide — só ajuda, ninguém é culpado por ela. */
  readonly dica: string | null;
}

export function leituraDasContagens(c: {
  readonly inconsistente: boolean;
  /** Unidades que o balconista não achou para repor (pedido − achado). */
  readonly faltou: number;
  readonly prateleira: number | null;
  /** null = ninguém contou o estoque; { total: null } = não conseguiu verificar. */
  readonly estoque: { readonly total: number | null } | null;
  readonly hosNaAbertura: number | null;
}): LeituraDasContagens {
  const total = c.estoque?.total ?? null;
  const fisicoTotal = c.prateleira !== null && total !== null ? c.prateleira + total : null;
  const nao = (semErro: string, dica: string | null = null): LeituraDasContagens => ({
    fisicoTotal,
    podeErro: false,
    semErro,
    dica,
  });
  if (c.inconsistente)
    return nao(
      'Caso de estoque do sistema (prateleira maior que o HOS): não é erro do balconista.',
      fisicoTotal === null ? null : `Na loja tem ${String(fisicoTotal)} no total.`,
    );
  if (c.estoque === null) return nao('Falta contar o estoque.');
  if (total === null) return nao('O estoque não foi verificado.');
  if (total === 0)
    return nao(
      'O estoque estava vazio.',
      'Não tinha nada lá embaixo: o balconista não tinha o que achar.',
    );
  if (c.hosNaAbertura !== null && c.hosNaAbertura <= 0)
    return nao(
      'Quando o caso abriu, o HOS marcava 0: a mercadoria pode ter chegado depois.',
      `O estoque tem ${String(total)} agora.`,
    );
  const dava = Math.min(total, c.faltou);
  return {
    fisicoTotal,
    podeErro: true,
    semErro: null,
    dica: `O estoque tem ${String(total)}: dava para repor ${String(dava)} ${unidade(dava)} das ${String(c.faltou)} que faltaram.`,
  };
}
